import type { Newsletter } from '@/main/entities/Account';
import * as S from './NewsletterBlockStyles';
import { Arrow } from 'icons/Arrow';
import useBreakpoint from '@/main/lib/hooks/useBreakpoint';
import { UserContext } from '@/main/lib/providers/UserProvider';
import { useContext, useEffect, useState } from 'react';
import getUserNewsletterSubscriptions from '@/main/lib/personalizationApi/getUserNewsletterSubscriptions';
import { subscribeUser } from '@/app_lib/utils/personalizationApi/user';
import { useRouter } from 'next/router';
import Modal from '../../Modal';
import useNewslettersModal from '@/main/pages/Newsletters/useNewslettersModal';
import { changeRoute } from '../../Globals/Base/Link';
import showLogin from '@/main/lib/services/Piano/showLogin';
import Cookies from 'js-cookie';
import { NoticeContext } from '@/main/lib/providers/NoticeProvider';

export interface NewsletterBlockProps {
  heading: string;
  heading_link: string;
  items?: Newsletter[];
  setLoading: React.Dispatch<React.SetStateAction<string>>;
}

const NewsletterBlock = ({ heading, items, setLoading }: NewsletterBlockProps) => {
  const isDesktop = useBreakpoint('lgMin');
  const isOneRow = isDesktop && items?.length < 4;
  const [newsletters, setNewsletters] = useState<Newsletter[]>(items);
  const formName = Cookies.get('newsletter_formName') || '';
  const { setNotification } = useContext(NoticeContext);

  const router = useRouter();
  const {
    userState: { email: userEmail, isLoggedIn },
  } = useContext(UserContext);

  const { isModalOpen, modalSettings, setIsModalOpen, setModalTarget, setModalType } = useNewslettersModal(
    userEmail,
    setNewsletters,
    setLoading,
    router.pathname,
  );

  const subscribeButton = (newsletter: Newsletter) => {
    if (!isLoggedIn) {
      showLogin();
      Cookies.set('newsletter_formName', newsletter.formName, {
        expires: new Date(new Date().getTime() + 60 * 60 * 1000),
      });
    } else {
      setModalType('subscribe');
      setModalTarget([newsletter]);
      setLoading('updating');
      subscribeUser(userEmail, '', [newsletter.formName], router.pathname)
        .then(() => {
          setNewsletters((prevUserNews) =>
            prevUserNews.map((n) => {
              if (n.formName === newsletter.formName) {
                return { ...n, subscriptionStatus: 'subscribed' };
              }
              return n;
            }),
          );
          setLoading('none');
          setIsModalOpen(true);
        })
        .catch(() => {
          setLoading('none');
          setNotification?.('error', `Your subscription to the ${newsletter.formName} could not be registered.`);
        });
    }
  };

  useEffect(() => {
    if (isLoggedIn && userEmail) {
      getUserNewsletterSubscriptions(userEmail).then((data) => {
        setNewsletters((prevList) =>
          prevList?.map((news) => ({
            ...news,
            subscriptionStatus: data.includes(news.formName) ? 'subscribed' : 'unsubscribed',
          })),
        );
      });
    }

    if (userEmail && formName) {
      const subscribeNewsletter = newsletters.filter((newsletter) => newsletter.formName === formName)[0];
      setModalType('subscribe');
      setModalTarget([subscribeNewsletter]);
      setLoading('updating');
      subscribeUser(userEmail, '', [subscribeNewsletter.formName], router.pathname)
        .then(() => {
          setNewsletters((prevUserNews) =>
            prevUserNews.map((n) => {
              if (n.formName === subscribeNewsletter.formName) {
                return { ...n, subscriptionStatus: 'subscribed' };
              }
              return n;
            }),
          );
          setLoading('none');
          setIsModalOpen(true);
          Cookies.remove('newsletter_formName');
        })
        .catch(() => {
          setLoading('none');
          setNotification?.(
            'error',
            `Your subscription to the ${subscribeNewsletter.formName} could not be registered.`,
          );
        });
    }
  }, [isLoggedIn, userEmail, formName]);

  return (
    <S.Wrapper>
      <Modal
        className={modalSettings.className}
        isOpened={isModalOpen}
        title={modalSettings.title}
        onClose={modalSettings.onClose}
        firstButton={
          modalSettings.firstButton
            ? {
                onClick: modalSettings.firstButton.onClick,
                title: modalSettings.firstButton.title,
                variant: modalSettings.firstButton.variant,
              }
            : undefined
        }
        secondButton={
          modalSettings.secondButton
            ? {
                onClick: modalSettings.secondButton.onClick,
                title: modalSettings.secondButton.title,
                variant: modalSettings.secondButton.variant,
              }
            : undefined
        }
      >
        {modalSettings.children}
      </Modal>
      <S.Heading>{heading}</S.Heading>
      <S.CTAButton onClick={() => void changeRoute(isLoggedIn ? '/my-account/newsletters' : '/newsletters')}>
        <div>{`More in ${heading}`}</div>
        <Arrow />
      </S.CTAButton>
      <S.ListAndImageWrapper>
        <S.NewslettersImage isOneRow={isOneRow} />
        <S.NewsletterListWrapper columnsCount={items?.length}>
          {newsletters?.map((news) => (
            <div>
              <S.Frequency>{news.frequency}</S.Frequency>
              <S.Title>{news.title}</S.Title>
              <S.AuthorWrapper>
                By
                <S.Author>{news.authors}</S.Author>
              </S.AuthorWrapper>
              <S.Excerpt>
                {news.description}
                {news.description}
              </S.Excerpt>
              <S.Button
                isSubscribed={news.subscriptionStatus === 'subscribed'}
                onClick={() => subscribeButton(news)}
                disabled={news.subscriptionStatus === 'subscribed'}
              >
                {news.subscriptionStatus === 'subscribed' ? 'SUBSCRIBED' : 'SUBSCRIBE NOW'}
              </S.Button>
            </div>
          ))}
        </S.NewsletterListWrapper>
      </S.ListAndImageWrapper>
    </S.Wrapper>
  );
};

export default NewsletterBlock;
